/**
 * Reviews.js — shared customer-reviews block for the public landing pages
 * (/, /gift, /celebration). Self-contained styles so it drops into any page.
 *
 * Displays one review "bubble" at a time in a swipeable carousel: swipe/drag
 * left-right on touch, arrows on desktop, dots to jump. Native CSS scroll-snap
 * does the swiping (no drag library); IntersectionObserver tracks the active card.
 *
 * Props:
 *   title?: string        — section heading (default "What families are saying")
 *   reviews?: array       — defaults to REVIEWS from constants/reviews
 *   maxWidth?: number     — cap the block width (default 760)
 */

import React, { useRef, useState, useEffect } from 'react';
import { REVIEWS, REVIEW_SUMMARY } from './constants/reviews';

const STYLES = `
.rv-section { width: 100%; margin: 0 auto 48px; }
.rv-head { text-align: center; margin-bottom: 22px; }
.rv-stars { color: #c9a84c; font-size: 1.1rem; letter-spacing: 2px; }
.rv-summary { font-family: 'Lora', serif; font-size: 0.85rem; color: #6b7280; margin-top: 4px; }
.rv-title { font-family: 'Cinzel', serif; font-size: 1.5rem; color: #0f172a; margin-top: 10px; }

.rv-carousel { position: relative; max-width: 600px; margin: 0 auto; }
.rv-track {
  position: relative;
  display: flex; align-items: stretch; gap: 16px;
  overflow-x: auto; scroll-snap-type: x mandatory; scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  padding: 4px 2px 6px;
  scrollbar-width: none;
}
.rv-track::-webkit-scrollbar { display: none; }
.rv-card {
  flex: 0 0 100%; scroll-snap-align: start;
  background: #fff; border: 1px solid rgba(15,23,42,0.08); border-radius: 16px;
  padding: 28px 26px; display: flex; flex-direction: column;
  box-shadow: 0 10px 30px rgba(15,23,42,0.06);
}
.rv-card-stars { color: #c9a84c; font-size: 1rem; letter-spacing: 1px; margin-bottom: 12px; }
.rv-card-text { font-family: 'Lora', serif; font-style: italic; font-size: 1.05rem; color: #1a1612; line-height: 1.6; flex: 1; }
.rv-card-name { font-family: 'Lora', serif; font-size: 0.9rem; font-weight: 700; color: #3c4653; margin-top: 16px; }
.rv-card-name::before { content: '— '; color: #c9a84c; }

.rv-dots { display: flex; justify-content: center; align-items: center; gap: 9px; margin-top: 16px; }
.rv-dot {
  -webkit-appearance: none; appearance: none; box-sizing: border-box; flex: 0 0 auto;
  width: 8px; height: 8px; min-width: 0; min-height: 0; line-height: 0;
  border-radius: 50%; border: none; padding: 0; margin: 0; cursor: pointer;
  background: rgba(15,23,42,0.22); transition: background .2s ease, transform .2s ease;
  -webkit-tap-highlight-color: transparent;
}
.rv-dot-active { background: #c9a84c; transform: scale(1.3); }

.rv-arrow {
  -webkit-appearance: none; appearance: none;
  position: absolute; top: 50%; transform: translateY(-50%); z-index: 2;
  width: 42px; height: 42px; border-radius: 50%; border: 1px solid rgba(15,23,42,0.12);
  background: #fff; color: #3c4653; font-size: 1.4rem; line-height: 1; cursor: pointer;
  box-shadow: 0 4px 14px rgba(15,23,42,0.10);
  display: none; align-items: center; justify-content: center;
  transition: color .15s ease, opacity .15s ease;
}
.rv-arrow:hover { color: #7c2d12; }
.rv-arrow:disabled { opacity: .3; cursor: default; }
.rv-arrow-prev { left: -20px; }
.rv-arrow-next { right: -20px; }

.rv-hint { text-align: center; font-family: 'Lora', serif; font-size: 0.72rem; letter-spacing: 0.03em; color: #b3ada2; margin-top: 11px; }

@media (min-width: 760px) {
  .rv-card { padding: 32px 36px; }
  .rv-arrow { display: flex; }
  .rv-hint { display: none; }
}
@media (max-width: 759px) {
  .rv-title { font-size: 1.3rem; }
}
`;

function Stars({ n = 5 }) {
  return <span aria-label={`${n} out of 5 stars`}>{'★'.repeat(n)}</span>;
}

export default function Reviews({ title = 'What families are saying', reviews = REVIEWS, maxWidth = 760 }) {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  // Track which card is centered in the viewport so the dots/arrows stay in sync
  // with manual swiping. Most-visible card (>=60%) wins.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const cards = Array.from(track.querySelectorAll('.rv-card'));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.6) {
            const idx = cards.indexOf(e.target);
            if (idx >= 0) setActive(idx);
          }
        });
      },
      { root: track, threshold: [0.6] }
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [reviews]);

  const goTo = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const cards = track.querySelectorAll('.rv-card');
    const clamped = Math.max(0, Math.min(i, cards.length - 1));
    const card = cards[clamped];
    if (card) track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
  };

  return (
    <section className="rv-section" style={{ maxWidth }}>
      <style>{STYLES}</style>
      <div className="rv-head">
        <div className="rv-stars"><Stars n={5} /></div>
        <div className="rv-summary">
          {REVIEW_SUMMARY.rating.toFixed(1)} on {REVIEW_SUMMARY.source} · {REVIEW_SUMMARY.count} reviews
        </div>
        <h2 className="rv-title">{title}</h2>
      </div>

      <div className="rv-carousel">
        <button className="rv-arrow rv-arrow-prev" aria-label="Previous review"
          onClick={() => goTo(active - 1)} disabled={active === 0}>‹</button>

        <div className="rv-track" ref={trackRef}>
          {reviews.map((r, i) => (
            <div className="rv-card" key={i}>
              <div className="rv-card-stars"><Stars n={r.rating} /></div>
              <p className="rv-card-text">"{r.text}"</p>
              <div className="rv-card-name">{r.name}</div>
            </div>
          ))}
        </div>

        <button className="rv-arrow rv-arrow-next" aria-label="Next review"
          onClick={() => goTo(active + 1)} disabled={active === reviews.length - 1}>›</button>
      </div>

      {reviews.length > 1 && (
        <>
          <div className="rv-dots">
            {reviews.map((_, i) => (
              <button key={i} className={`rv-dot${i === active ? ' rv-dot-active' : ''}`}
                aria-label={`Go to review ${i + 1}`} onClick={() => goTo(i)} />
            ))}
          </div>
          <div className="rv-hint">Swipe to read more →</div>
        </>
      )}
    </section>
  );
}
